import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useAccount } from 'wagmi';
import { FaArrowUp, FaArrowDown, FaEquals, FaUndo, FaHourglassHalf, FaChevronDown, FaTimes } from 'react-icons/fa';
import { useT } from '../../../i18n';
import {
  useGetRounds,
  useGetCurrentEpoch,
  useGetLastOraclePrice,
  useGetConfig,
  useGetRoundExtras,
  usePredictions,
} from '../context/PredictionsContext';
import { useBullvsBearContract } from '../hooks/useBullvsBearContract';
import { useRoundPriceHistory } from '../hooks/useRoundPriceHistory';
import { BetPanel, ClaimButton } from './BettingComponents';
import { AnimatedPrice, WinToast } from './Effects';
import RoundChart from './RoundChart';
import CountdownTimer from './CountdownTimer';
import { useChainNow, SIDE_STYLE, Badge, InfoRow, PoolBar, PositionBox, ExpiredCard } from './RoundCard';
import {
  roundPool,
  sideMultiplier,
  nextMultiplier,
  roundOutcome,
  userPayout,
  priceSide,
  toUsdt,
  formatUsdt,
  formatPrice,
  REFUND_DELAY_SECONDS,
} from '../utils/roundMath';

const RESULTS_SHOWN = 6;

/** "Details" toggle: the numbers most players do not need to decide */
const Details = ({ children, label }) => {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-center gap-1.5 py-1 text-xs font-semibold text-white/45 hover:text-white/80"
      >
        {open ? t('bvb.hideDetails') : label || t('bvb.details')}
        <FaChevronDown size={10} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="mt-2 space-y-2 rounded-xl border border-white/10 bg-black/30 p-3">{children}</div>}
    </div>
  );
};

/** The three steps of a round, one line each */
const HowItWorks = ({ minutes }) => {
  const t = useT();
  const steps = [t('bvb.howStep1'), t('bvb.howStep2'), t('bvb.howStep3', { min: minutes })];
  return (
    <ol className="space-y-1.5">
      {steps.map((text, i) => (
        <li key={i} className="flex items-start gap-2 text-[11px] leading-snug text-white/60">
          <span className="lb-num flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand text-[9px] font-bold text-white">
            {i + 1}
          </span>
          {text}
        </li>
      ))}
    </ol>
  );
};

/* ---------- the bet on the next round ---------- */

const BetCard = ({ epoch, round, referralAddress }) => {
  const t = useT();
  const config = useGetConfig();
  const extras = useGetRoundExtras(epoch);
  const pool = roundPool(round, extras);
  const { useUserBet } = useBullvsBearContract();
  const bet = useUserBet(epoch);
  const earlyBirdLeft = extras.earlyBirds !== undefined ? Math.max(0, config.earlyBirdCount - extras.earlyBirds) : 0;
  const multipliers = { up: nextMultiplier(pool, 'up', config), down: nextMultiplier(pool, 'down', config) };
  const betMultipliers = { up: sideMultiplier(pool, 'up', config), down: sideMultiplier(pool, 'down', config) };

  return (
    <section className="flex flex-col gap-4 rounded-3xl border-2 border-brand/60 bg-gradient-to-b from-[#190d25] to-[#06030a] p-5 shadow-[0_0_60px_-24px_rgba(142,63,204,0.9)]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className="lb-num text-xs font-bold text-white/40">#{epoch}</span>
          <h2 className="lb-heading text-2xl leading-tight text-white">{t('bvb.betNow')}</h2>
          <p className="text-[11px] text-white/45">{t('bvb.howStep1')}</p>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-wider text-white/45">{t('bvb.betsCloseIn')}</div>
          <CountdownTimer target={round.lockTimestamp} variant="boxes" className="mt-1 justify-end" />
        </div>
      </div>

      {bet ? (
        <PositionBox bet={bet}>
          <InfoRow label={t('bvb.ifYouWin')} value={`≈ ${formatUsdt(toUsdt(bet.amount) * betMultipliers[bet.position])} USDT`} />
          <p className="text-[11px] text-white/45">{t('bvb.waitingStart')}</p>
        </PositionBox>
      ) : (
        <BetPanel epoch={epoch} pool={pool} config={config} earlyBirdLeft={earlyBirdLeft} referralAddress={referralAddress} />
      )}

      <Details label={t('bvb.howAndDetails')}>
        <HowItWorks minutes={Math.round(config.intervalSeconds / 60)} />
        <div className="my-1 border-t border-white/5" />
        <p className="text-[11px] font-semibold uppercase tracking-wide text-white/40">{t('bvb.splitTitle')}</p>
        <PoolBar pool={pool} multipliers={multipliers} />
        <InfoRow label={t('bvb.totalPool')} value={`${formatUsdt(pool.total)} USDT`} />
        {pool.house > 0 && <InfoRow label={t('bvb.houseSeed')} value={`${formatUsdt(pool.house)} USDT`} />}
        {earlyBirdLeft > 0 && (
          <InfoRow label={t('bvb.earlyBird')} value={t('bvb.earlyBirdSlots', { n: earlyBirdLeft, bonus: config.earlyBirdBonusBps / 100 })} />
        )}
        <p className="text-[11px] text-white/40">{t('bvb.feeNote', { fee: config.feeBps / 100 })}</p>
      </Details>
    </section>
  );
};

/* ---------- the round being decided: prices and chart ---------- */

const Stat = ({ label, children, className = '' }) => (
  <div className={`min-w-0 ${className}`}>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-white/40">{label}</div>
    <div className="mt-1">{children}</div>
  </div>
);

const LivePanel = ({ epoch, round, oraclePrice, mobile }) => {
  const t = useT();
  const config = useGetConfig();
  const extras = useGetRoundExtras(epoch);
  const pool = roundPool(round, extras);
  const now = useChainNow();
  const { useUserBet } = useBullvsBearContract();
  const bet = useUserBet(epoch);
  const lock = Number(round.lockPrice);
  const price = Number(oraclePrice);
  const side = priceSide(price, lock);
  const s = SIDE_STYLE[side || 'tie'];
  const delta = price && lock ? price - lock : 0;
  const lockTs = Number(round.lockTimestamp);
  const closeTs = Number(round.closeTimestamp);
  const overdue = closeTs && now > closeTs;
  const willRefund = closeTs && now > closeTs + REFUND_DELAY_SECONDS;
  const multipliers = { up: sideMultiplier(pool, 'up', config), down: sideMultiplier(pool, 'down', config) };

  // the feed's prices since the round started, plus the prices polled while the page is open
  const history = useRoundPriceHistory(lockTs, oraclePrice);
  const [polled, setPolled] = useState({ epoch, points: [] });
  useEffect(() => {
    if (!price) return;
    setPolled((prev) => ({ epoch, points: [...(prev.epoch === epoch ? prev.points : []), { t: Math.floor(now), p: price }].slice(-200) }));
    // a new sample per price change, stamped on the chain clock
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price, epoch]);
  // the feed history when it is readable (polled prices otherwise), ending on the price shown above
  const series = [{ t: lockTs, p: lock }, ...(history.length ? history : polled.epoch === epoch ? polled.points : [])]
    .filter((point) => point.t >= lockTs && point.p)
    .sort((a, b) => a.t - b.t);
  const points = price && series.length ? [...series, { t: Math.max(series[series.length - 1].t, Math.floor(now)), p: price }] : series;

  return (
    <section className="flex flex-col gap-4 rounded-3xl border border-white/10 bg-[#08050d]/90 p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Badge className="border-amber-400/40 bg-amber-500/15 text-amber-300">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-300" /> {t('bvb.runningNow')}
          </Badge>
          <span className="lb-num text-sm font-bold text-white/50">#{epoch}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-white/45">
          {t('bvb.endsIn')}
          <CountdownTimer target={round.closeTimestamp} className="text-base text-white" />
        </div>
      </div>

      <div className="relative isolate grid grid-cols-2 gap-4 sm:grid-cols-[1.4fr_1fr_1fr]">
        {s.Animal && (
          <s.Animal aria-hidden="true" className={`pointer-events-none absolute -top-2 right-0 -z-10 h-20 w-20 opacity-[0.08] ${s.text}`} />
        )}
        <Stat label={t('bvb.currentPrice')} className="col-span-2 sm:col-span-1">
          <div className={`text-3xl font-extrabold sm:text-4xl ${s.text}`}>
            <AnimatedPrice value={price} className="-ml-1.5" />
          </div>
        </Stat>
        <Stat label={t('bvb.lockPrice')}>
          <div className="lb-num text-xl font-bold text-white sm:text-2xl">{formatPrice(lock)}</div>
        </Stat>
        <Stat label={t('bvb.change')}>
          <div className={`lb-num text-xl font-bold sm:text-2xl ${s.text}`}>
            {delta >= 0 ? '+' : '−'}${Math.abs(delta).toFixed(2)}
          </div>
          {side && side !== 'tie' ? (
            <div className={`mt-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${s.soft}`}>
              <s.Icon size={9} /> {side === 'up' ? t('bvb.up') : t('bvb.down')} {t('bvb.winningNow').toLowerCase()}
            </div>
          ) : (
            <div className="mt-1 text-[11px] text-white/50">{t('bvb.tie')}</div>
          )}
        </Stat>
      </div>

      <div className="rounded-2xl border border-white/5 bg-black/30 px-2 pt-2">
        <RoundChart points={points} lock={lock} start={lockTs} end={closeTs} height={mobile ? 200 : 280} />
      </div>

      {overdue && (
        <div className="rounded-xl border border-orange-400/30 bg-orange-500/10 px-3 py-2 text-xs text-orange-200">
          <FaHourglassHalf className="mr-1 inline" />
          {willRefund ? t('bvb.refundSoon') : t('bvb.overdueBody')}
        </div>
      )}

      <div className={`grid gap-4 ${bet ? 'sm:grid-cols-2' : ''}`}>
        <div className="space-y-2">
          <PoolBar pool={pool} multipliers={multipliers} winner={side === 'tie' ? null : side} />
          <InfoRow label={t('bvb.totalPool')} value={`${formatUsdt(pool.total)} USDT`} />
        </div>
        {bet && (
          <PositionBox bet={bet}>
            <InfoRow
              label={t('bvb.status')}
              value={side === bet.position ? t('bvb.winningNow') : side === 'tie' ? t('bvb.tie') : t('bvb.losingNow')}
              className={side === bet.position ? 'text-emerald-300' : 'text-rose-300'}
            />
            <InfoRow label={t('bvb.ifYouWin')} value={`≈ ${formatUsdt(toUsdt(bet.amount) * multipliers[bet.position])} USDT`} />
          </PositionBox>
        )}
      </div>
    </section>
  );
};

/* ---------- the timeline: past rounds, the live one and the next one, left to right ---------- */

const OUTCOME_PILL = {
  up: ['bg-emerald-500/20 text-emerald-300', FaArrowUp],
  down: ['bg-rose-500/20 text-rose-300', FaArrowDown],
  tie: ['bg-amber-500/20 text-amber-200', FaEquals],
  refund: ['bg-orange-500/20 text-orange-200', FaUndo],
  refund_pending: ['bg-orange-500/20 text-orange-200', FaHourglassHalf],
  awaiting: ['bg-white/10 text-white/60', FaHourglassHalf],
};

const BetMark = ({ bet, toClaim }) => {
  const t = useT();
  if (!bet) return null;
  return (
    <span
      className={`absolute -top-2 right-2 rounded-full px-1.5 py-px text-[9px] font-bold ${
        toClaim ? 'bg-brand text-white' : 'bg-white/15 text-white/70'
      }`}
    >
      {toClaim ? t('bvb.toClaim') : bet.position === 'up' ? '▲' : '▼'}
    </span>
  );
};

const PastPill = ({ epoch, round, selected, onSelect, onStatus }) => {
  const t = useT();
  const extras = useGetRoundExtras(epoch);
  const now = useChainNow();
  const { useUserBet } = useBullvsBearContract();
  const bet = useUserBet(epoch);
  const outcome = roundOutcome(round, extras, now);
  const [cls, Icon] = OUTCOME_PILL[outcome];
  const payout = userPayout(round, extras, bet, outcome);
  const toClaim = !!bet && !bet.claimed && payout > 0;
  const won = !!bet && outcome === bet.position && payout > 0;
  useEffect(() => {
    onStatus(epoch, toClaim ? { payout, won } : null);
  }, [epoch, toClaim, payout, won, onStatus]);
  const lock = Number(round.lockPrice);
  const close = Number(round.closePrice);
  const settled = round.oracleCalled && lock && close;
  const text = outcome === 'up' ? 'text-emerald-300' : outcome === 'down' ? 'text-rose-300' : 'text-white/60';

  return (
    <button
      onClick={onSelect}
      className={`relative flex min-w-[112px] flex-1 flex-col gap-1 rounded-xl border px-3 py-2 text-left transition-colors ${
        selected ? 'border-[#a369d9] bg-brand/15' : 'border-white/10 bg-white/[0.03] hover:border-white/25'
      }`}
    >
      <BetMark bet={bet} toClaim={toClaim} />
      <span className="flex items-center justify-between gap-2">
        <span className="lb-num text-[11px] font-bold text-white/50">#{epoch}</span>
        <span className={`flex h-5 w-5 items-center justify-center rounded-md ${cls}`}>
          <Icon size={9} />
        </span>
      </span>
      <span className={`lb-num text-sm font-bold ${text}`}>
        {settled ? `${close >= lock ? '+' : '−'}$${Math.abs(close - lock).toFixed(2)}` : t('bvb.awaiting')}
      </span>
    </button>
  );
};

const LivePill = ({ epoch, round, oraclePrice }) => {
  const t = useT();
  const { useUserBet } = useBullvsBearContract();
  const bet = useUserBet(epoch);
  const lock = Number(round.lockPrice);
  const price = Number(oraclePrice);
  const side = priceSide(price, lock);
  const delta = price && lock ? price - lock : 0;
  const s = SIDE_STYLE[side || 'tie'];
  return (
    <div className="relative flex min-w-[124px] flex-1 flex-col gap-1 rounded-xl border border-amber-400/50 bg-amber-500/10 px-3 py-2">
      <BetMark bet={bet} />
      <span className="flex items-center justify-between gap-2">
        <span className="lb-num text-[11px] font-bold text-amber-200">#{epoch}</span>
        <span className="flex items-center gap-1 text-[10px] font-bold uppercase text-amber-300">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-300" /> {t('bvb.live')}
        </span>
      </span>
      <span className={`lb-num text-sm font-bold ${s.text}`}>
        {delta >= 0 ? '+' : '−'}${Math.abs(delta).toFixed(2)}
      </span>
    </div>
  );
};

const NextPill = ({ epoch, round }) => {
  const t = useT();
  return (
    <div className="relative flex min-w-[124px] flex-1 flex-col gap-1 rounded-xl border border-[#a369d9]/60 bg-brand/15 px-3 py-2">
      <span className="flex items-center justify-between gap-2">
        <span className="lb-num text-[11px] font-bold text-[#c9acea]">#{epoch}</span>
        <span className="text-[10px] font-bold uppercase text-[#c9acea]">{t('bvb.next')}</span>
      </span>
      <span className="flex items-center gap-1.5 text-[11px] text-white/60">
        {t('bvb.betsOpen')} <CountdownTimer target={round.lockTimestamp} className="text-sm text-white" />
      </span>
    </div>
  );
};

const celebratedKey = (address, epoch) => `bvb-won-${address?.toLowerCase()}-${epoch}`;
const wasCelebrated = (address, epoch) => {
  try {
    return localStorage.getItem(celebratedKey(address, epoch)) === '1';
  } catch (e) {
    return false;
  }
};
const markCelebrated = (address, epoch) => {
  try {
    localStorage.setItem(celebratedKey(address, epoch), '1');
  } catch (e) {
    // private mode: the toast may show again on reload
  }
};

const RoundTimeline = ({ past, rounds, liveEpoch, nextEpoch, oraclePrice }) => {
  const t = useT();
  const config = useGetConfig();
  const { address } = useAccount();
  const { claimWinnings } = useBullvsBearContract();
  const { fetchContractData } = usePredictions();
  const [selected, setSelected] = useState(null);
  const [claimables, setClaimables] = useState({});
  const [win, setWin] = useState(null);
  const [collecting, setCollecting] = useState(false);
  const scroller = useRef(null);
  const shown = selected !== null && rounds[selected] ? selected : null;
  const extras = useGetRoundExtras(shown);
  const live = rounds[liveEpoch];
  const next = rounds[nextEpoch] || { epoch: nextEpoch };

  const onStatus = useCallback((epoch, status) => {
    setClaimables((prev) => {
      if (!status && !(epoch in prev)) return prev;
      const nextState = { ...prev };
      if (status) nextState[epoch] = status;
      else delete nextState[epoch];
      return nextState;
    });
  }, []);

  const pending = Object.entries(claimables).filter(([e]) => past.includes(Number(e)));
  const total = pending.reduce((sum, [, c]) => sum + c.payout, 0);
  const newestWin = pending
    .filter(([, c]) => c.won)
    .map(([e]) => Number(e))
    .sort((a, b) => b - a)[0];

  // the newest rounds sit on the right: start scrolled there on narrow screens
  useEffect(() => {
    if (scroller.current) scroller.current.scrollLeft = scroller.current.scrollWidth;
  }, [nextEpoch]);

  // celebrate the newest win once per round and wallet
  useEffect(() => {
    if (!address || win || !newestWin || wasCelebrated(address, newestWin)) return;
    markCelebrated(address, newestWin);
    setWin({ epoch: newestWin, payout: claimables[newestWin].payout });
  }, [address, newestWin, claimables, win]);

  useEffect(() => {
    if (!win) return undefined;
    const id = setTimeout(() => setWin(null), 9000);
    return () => clearTimeout(id);
  }, [win]);

  const collectAll = async () => {
    setCollecting(true);
    const result = await claimWinnings(pending.map(([e]) => Number(e)));
    setCollecting(false);
    if (result?.success) fetchContractData();
  };

  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-[#08050d]/80 p-3 sm:p-4">
      <div className="flex items-center justify-between gap-3 px-1">
        <div className="flex items-baseline gap-2">
          <h3 className="text-xs font-bold uppercase tracking-wider text-white/70">{t('bvb.rounds')}</h3>
          <span className="text-[11px] text-white/35">{t('bvb.tapForDetails')}</span>
        </div>
        {pending.length > 1 && (
          <button
            onClick={collectAll}
            disabled={collecting}
            className="rounded-lg bg-gradient-to-r from-brand to-fuchsia-500 px-3 py-1.5 text-xs font-bold text-white shadow-lg disabled:opacity-50"
          >
            {collecting ? t('bvb.claiming') : t('bvb.collectAll', { amount: formatUsdt(total) })}
          </button>
        )}
      </div>
      <div ref={scroller} className="flex gap-2 overflow-x-auto pb-1 pt-2" style={{ scrollbarWidth: 'none' }}>
        {past.map((e) => (
          <PastPill
            key={e}
            epoch={e}
            round={rounds[e]}
            selected={shown === e}
            onSelect={() => setSelected(shown === e ? null : e)}
            onStatus={onStatus}
          />
        ))}
        {live && <LivePill epoch={liveEpoch} round={live} oraclePrice={oraclePrice} />}
        {nextEpoch >= 1 && <NextPill epoch={nextEpoch} round={next} />}
      </div>
      {shown !== null && (
        <div className="relative mx-auto w-full max-w-lg rounded-2xl border border-white/10 bg-black/40 p-4">
          <button
            onClick={() => setSelected(null)}
            aria-label={t('bvb.close')}
            className="absolute right-3 top-3 text-white/40 hover:text-white"
          >
            <FaTimes size={12} />
          </button>
          <ExpiredCard round={rounds[shown]} epoch={shown} pool={roundPool(rounds[shown], extras)} config={config} extras={extras} />
        </div>
      )}
      <WinToast win={win} onClose={() => setWin(null)} collect={win && <ClaimButton epoch={win.epoch} amount={win.payout} />} />
    </section>
  );
};

/**
 * Bull vs Bear board: the rounds as a timeline on top (oldest on the left), the round being decided with its chart
 * as the main panel and the bet on the next round beside it. On phones: timeline, live round, bet.
 */
const GameBoard = ({ referralAddress, mobile = false }) => {
  const rounds = useGetRounds();
  const oraclePrice = useGetLastOraclePrice();
  const currentEpoch = Number(useGetCurrentEpoch() ?? 0);
  const liveEpoch = currentEpoch - 1;
  const live = rounds[liveEpoch];
  const next = rounds[currentEpoch] || { epoch: currentEpoch };
  const past = [];
  for (let e = currentEpoch - 2; e >= 1 && past.length < RESULTS_SHOWN; e--) {
    if (rounds[e]) past.unshift(e);
  }

  // each new round slides in: the bet card becomes the live round and a new bet card opens
  const liveBox = live ? (
    <motion.div
      key={`live-${liveEpoch}`}
      initial={{ opacity: 0, x: mobile ? 0 : 40, y: mobile ? -16 : 0 }}
      animate={{ opacity: 1, x: 0, y: 0 }}
      transition={{ type: 'spring', stiffness: 220, damping: 26 }}
    >
      <LivePanel epoch={liveEpoch} round={live} oraclePrice={oraclePrice} mobile={mobile} />
    </motion.div>
  ) : null;
  const betBox =
    currentEpoch >= 1 ? (
      <motion.div
        key={`bet-${currentEpoch}`}
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 220, damping: 24, delay: 0.1 }}
      >
        <BetCard epoch={currentEpoch} round={next} referralAddress={referralAddress} />
      </motion.div>
    ) : null;
  const timeline = (
    <RoundTimeline past={past} rounds={rounds} liveEpoch={liveEpoch} nextEpoch={currentEpoch} oraclePrice={oraclePrice} />
  );

  if (mobile) {
    return (
      <div className="flex flex-col gap-4 px-3">
        {timeline}
        {liveBox}
        {betBox}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-5 px-5">
      {timeline}
      <div className="grid grid-cols-[minmax(0,1.75fr)_minmax(0,1fr)] items-start gap-5">
        <div>{liveBox}</div>
        <div className="sticky top-24">{betBox}</div>
      </div>
    </div>
  );
};

export default GameBoard;
